/**
 * Concert-grade lasers: a multi-projector beam show and a "liquid sky" laser sheet through haze,
 * plus a crowd silhouette layer to put in front of them.
 *
 * What makes real laser photos read: hair-thin cores, beams saturating to white where they pile
 * up, brightness that varies along each beam as it passes through smoke, and a faint wash of
 * light scattered into the room.
 */
import type { VisualizationDef } from '../../../shared/viz';

export const LASER_PATTERNS = [
  'Fan sweep',
  'Crossfire',
  'Tunnel',
  'Wave',
  'Chase',
  'Shutter',
] as const;

export const laserShow: VisualizationDef = {
  id: 'laser-show',
  name: 'Laser Show',
  description:
    'A rig of laser projectors along the stage, each throwing a dense fan of hair-thin beams through haze. The fans cross into a glowing lattice and move with the music.',
  tags: ['energy:high', 'style:lasers'],
  role: 'background',
  params: [
    { id: 'projectors', label: 'Projectors', type: 'float', min: 1, max: 7, default: 5, step: 1 },
    { id: 'beams', label: 'Beams each', type: 'float', min: 4, max: 32, default: 18, step: 1 },
    {
      id: 'pattern',
      label: 'Pattern',
      type: 'float',
      min: 0,
      max: LASER_PATTERNS.length - 1,
      default: 1,
      step: 1,
      control: 'select',
      options: [...LASER_PATTERNS],
    },
    { id: 'spread', label: 'Fan width', type: 'float', min: 0.2, max: 2.5, default: 1.3 },
    { id: 'speed', label: 'Speed', type: 'float', min: 0, max: 2, default: 0.6 },
    {
      id: 'colour',
      label: 'Colour',
      type: 'float',
      min: 0,
      max: 2,
      default: 0,
      step: 1,
      control: 'select',
      options: ['One colour', 'Colour per projector', 'Rainbow beams'],
    },
    { id: 'haze', label: 'Haze', type: 'float', min: 0, max: 1.5, default: 0.7 },
    { id: 'width', label: 'Beam width', type: 'float', min: 0.5, max: 3, default: 1 },
  ],
  fragment: /* glsl */ `
// One beam at p: x = its crisp core, y = the scatter/bloom around it.
vec2 laser(vec2 p, vec2 o, vec2 d, float w, out float along) {
  vec2 v = p - o;
  along = dot(v, d);
  if (along < 0.0) return vec2(0.0);
  float dist = length(v - d * along);
  float ww = w * (1.0 + along * 0.12);
  return vec2(exp(-dist * dist / (ww * ww)), 0.16 * exp(-dist / (ww * 5.0)) + 0.03 * exp(-dist / (ww * 35.0)));
}
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float aspect = uResolution.x / uResolution.y;
  int np = int(uP[0]);
  int nb = int(uP[1]);
  int pat = int(uP[2] + 0.5);
  float spread = uP[3];
  float t = uTime * uP[4];
  float beats = uBeats;
  // At least about a pixel wide, so thin beams stay smooth instead of breaking up.
  float w = max(0.0011 * uP[7], 0.9 / uResolution.y);
  // Smoke: patchy, drifting; beams are only visible where there's something to scatter off.
  float smoke = hazeAt(p, uP[6]);
  vec3 col = vec3(0.0);
  for (int i = 0; i < 7; i++) {
    if (i >= np) break;
    float fi = float(i);
    float u = np > 1 ? fi / float(np - 1) : 0.5;
    vec2 o = vec2((u - 0.5) * aspect * 0.78, -0.24 + 0.03 * sin(fi * 2.3));
    if (p.y < o.y - 0.02 && pat != 2) continue;
    float side = u - 0.5;
    // Where this projector aims (radians from straight up) and how its fan is shaped.
    float aim = 0.0;
    if (pat == 0) aim = sin(t + fi * 0.9) * 0.45;
    else if (pat == 1) aim = -side * 0.9 + sin(t * 0.8 + fi) * 0.2;
    else if (pat == 3) aim = sin(t * 0.7) * 0.2;
    else if (pat == 5) aim = sin(t * 0.5 + fi) * 0.3;
    vec2 focus = vec2(0.35 * sin(t * 0.6), 0.32 + 0.08 * cos(t * 0.9));
    float shutter = pat == 5 ? step(0.5, mod(floor(beats) + fi, 2.0)) : 1.0;
    if (shutter < 0.5) continue;
    vec3 projCol = uP[5] > 0.5 && uP[5] < 1.5 ? palette(u * 0.8 + 0.1) : palette(0.5);
    for (int j = 0; j < 32; j++) {
      if (j >= nb) break;
      float fj = nb > 1 ? float(j) / float(nb - 1) : 0.5;
      float ang = aim + (fj - 0.5) * spread;
      if (pat == 2) {
        // Tunnel: every beam aims near one moving point, slightly fanned.
        vec2 to = normalize(focus - o);
        ang = atan(to.x, to.y) + (fj - 0.5) * spread * 0.25;
      } else if (pat == 3) {
        ang += sin(t * 2.0 + fj * 6.0 + fi) * 0.25;
      }
      vec2 d = vec2(sin(ang), cos(ang));
      float along;
      vec2 b = laser(p, o, d, w, along);
      if (b.x + b.y < 1e-4) continue;
      float gate = 1.0;
      if (pat == 4) gate = 0.15 + 0.85 * step(fract(fj - beats * 0.25 * uP[4] * 2.0 + fi * 0.13), 0.22);
      // Brightness varies along the beam as it passes through thicker and thinner smoke.
      float wisp = 0.7 + 0.3 * sin(along * 23.0 + fj * 7.0 + uTime * 1.3 + fi);
      float band = uBands[(i + j) % 8];
      vec3 c = uP[5] > 1.5 ? palette(fract(fj + fi * 0.21)) : projCol;
      float e = gate * wisp * exp(-along * 0.3) * (0.7 + 1.1 * band + 0.5 * pulse());
      // The core runs hot (towards white); the glow keeps the laser's colour.
      col += (mix(c, vec3(1.0), 0.55) * b.x * 2.2 + c * b.y * 2.0) * e;
    }
    // The projector's aperture glow.
    col += projCol * (exp(-length(p - o) * 60.0) * 2.0 + exp(-length(p - o) * 12.0) * 0.25) * (0.6 + uLevel);
  }
  col *= smoke;
  // Light scattered into the room: a coloured wash that's strongest up in the haze.
  vec3 wash = uP[5] > 0.5 ? palette(0.5 + 0.3 * p.x) : palette(0.5);
  col += wash * (0.05 + 0.07 * smoothstep(-0.25, 0.45, p.y)) * smoke * (0.7 + uLevel + 0.6 * pulse()) * float(np) / 5.0;
  // Piled-up beams burn to white, the way they do on camera.
  col = 1.0 - exp(-col * 1.4);
  fragColor = vec4(col, 1.0);
}`,
};

export const liquidSky: VisualizationDef = {
  id: 'liquid-sky',
  name: 'Liquid Sky',
  description:
    'A laser sheet scanned flat across the room, seen from below: the haze turns it into a churning, glowing ceiling with bright edges. The bass stirs the smoke.',
  tags: ['energy:mid', 'style:lasers'],
  role: 'background',
  params: [
    { id: 'height', label: 'Sheet height', type: 'float', min: 0.3, max: 2, default: 1 },
    { id: 'flow', label: 'Smoke flow', type: 'float', min: 0, max: 2, default: 0.6 },
    { id: 'detail', label: 'Smoke detail', type: 'float', min: 0.3, max: 2, default: 1 },
    { id: 'fan', label: 'Sheet width', type: 'float', min: 0.5, max: 3, default: 2.2 },
    { id: 'wave', label: 'Wave', type: 'float', min: 0, max: 1, default: 0.3 },
    {
      id: 'colour',
      label: 'Colour',
      type: 'float',
      min: 0,
      max: 1,
      default: 0,
      step: 1,
      control: 'select',
      options: ['Palette', 'Classic cyan & green'],
    },
    { id: 'edges', label: 'Edge beams', type: 'float', min: 0, max: 1, default: 0.7 },
  ],
  fragment: /* glsl */ `
// Ridged noise: sharp creases, like the filaments in real smoke.
float ridged(vec2 q) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 4; i++) { v += a * (1.0 - abs(noise2(q) * 2.0 - 1.0)); q = q * 2.07 + vec2(3.3, 7.1); a *= 0.5; }
  return v / 0.9375;
}
float smokeAt(vec2 q, float t) {
  vec2 w = vec2(fbm(q * 0.5 + vec2(0.0, t * 0.4)), fbm(q * 0.5 + vec2(5.2, 1.3) - vec2(t * 0.3, 0.0)));
  float body = fbm(q * 0.9 + w * 2.0 + vec2(t * 0.2, -t * 0.5));
  float wisps = ridged(q * vec2(2.6, 1.8) * uP[2] + w * 3.0 + vec2(t * 0.5, -t * 0.2));
  return mix(body, body * wisps * 1.6, 0.6);
}
void main() {
  vec2 uv = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float horizon = -0.28;
  vec3 col = vec3(0.0);
  float t = uTime * uP[1] * (0.6 + 0.8 * uBass);
  // The projector sits at the bottom centre; the sheet spreads out above the viewer.
  if (uv.y > horizon + 0.002) {
    float sy = uv.y - horizon;
    // Perspective: screen height above the horizon → distance across the sheet.
    float wave = uP[4] * 0.06 * sin(uv.x * 2.5 + uTime * 0.8);
    float z = uP[0] * 0.35 / (sy + wave * sy);
    vec2 q = vec2(uv.x * z, z);
    // The projector is far off, near the horizon; its fan of light spreads back towards us.
    vec2 proj = vec2(0.0, 14.0);
    vec2 fromProj = q - proj;
    float ang = atan(fromProj.x, -fromProj.y);
    float fan = uP[3] * 0.5;
    float inFan = smoothstep(fan, fan * 0.88, abs(ang)) * step(fromProj.y, 0.0);
    float r = length(fromProj);
    float fall = 1.0 / (1.0 + r * 0.06);
    float s = smokeAt(q * 0.9, t);
    float lit = smoothstep(0.2, 0.8, s);
    lit = lit * lit * (3.0 - 2.0 * lit);
    // Far away the detail is finer than a pixel: settle to its average.
    lit = mix(lit, 0.45, smoothstep(8.0, 24.0, z));
    vec3 a = uP[5] > 0.5 ? vec3(0.1, 0.55, 1.0) : palette(0.3 + 0.2 * s);
    vec3 b = uP[5] > 0.5 ? vec3(0.2, 1.0, 0.55) : palette(0.75 + 0.15 * s);
    vec3 sheet = mix(a, b, smoothstep(0.2, 0.9, s + 0.2 * sin(ang * 2.0 + uTime * 0.3)));
    col += sheet * lit * inFan * fall * (2.4 + 1.0 * uLevel + 0.6 * pulse());
    // The densest smoke scatters so much light it reads near-white.
    vec3 hot = uP[5] > 0.5 ? vec3(0.8, 1.0, 1.0) : mix(b, vec3(1.0), 0.6);
    col += hot * smoothstep(0.62, 0.9, s) * inFan * fall * (1.6 + 0.8 * pulse()) * (1.0 - smoothstep(10.0, 24.0, z));
    // The fan's edges, where the scanning beam turns round, are brighter lines.
    float edge = exp(-pow((abs(ang) - fan * 0.95) * r * 6.0, 2.0)) * fall;
    col += b * edge * uP[6] * 0.8;
  }
  // The sheet seen edge-on near the horizon: a hot line, and the projector's flare.
  float lineDist = abs(uv.y - horizon);
  vec3 lineCol = uP[5] > 0.5 ? vec3(0.4, 1.0, 0.5) : palette(0.8);
  col += lineCol * (exp(-lineDist * 900.0) * 1.2 + exp(-lineDist * 90.0) * 0.25) * smoothstep(0.9, 0.0, abs(uv.x)) * uP[6];
  vec2 src = vec2(0.0, horizon);
  col += lineCol * (exp(-length(uv - src) * 70.0) * 2.0 + exp(-length(uv - src) * 10.0) * 0.2);
  col = 1.0 - exp(-col * 1.3);
  fragColor = vec4(col, 1.0);
}`,
};

export const crowd: VisualizationDef = {
  id: 'crowd',
  name: 'Crowd',
  description:
    'A crowd in silhouette along the bottom of the screen, heads bobbing and hands going up on the beat. Put it on top of lasers or anything else (it uses the Multiply blend).',
  tags: ['energy:high', 'style:lasers'],
  role: 'element',
  blend: 'multiply',
  params: [
    { id: 'height', label: 'Height', type: 'float', min: 0.1, max: 0.5, default: 0.22 },
    { id: 'density', label: 'People', type: 'float', min: 10, max: 60, default: 32, step: 1 },
    { id: 'hands', label: 'Hands up', type: 'float', min: 0, max: 1, default: 0.35 },
    { id: 'bounce', label: 'Bounce', type: 'float', min: 0, max: 2, default: 1 },
  ],
  fragment: /* glsl */ `
float capsule(vec2 p, vec2 a, vec2 b, float r) {
  vec2 pa = p - a, ba = b - a;
  float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
  return length(pa - ba * h) - r;
}
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float aspect = uResolution.x / uResolution.y;
  float base = -0.5;
  float H = uP[0];
  float n = floor(uP[1]);
  float spacing = aspect / n;
  float d = 1e9;
  // Two rows: the back row smaller and offset.
  for (int row = 0; row < 2; row++) {
    float fr = float(row);
    float scale = fr < 0.5 ? 1.0 : 0.8;
    float x = p.x / spacing + fr * 0.5 + aspect;
    float i0 = floor(x);
    for (int k = -1; k <= 1; k++) {
      float id = i0 + float(k) + fr * 100.0;
      float cx = (i0 + float(k) + 0.5 + (hash21(vec2(id, 1.0)) - 0.5) * 0.6 - fr * 0.5 - aspect) * spacing;
      float h = H * scale * (0.75 + 0.35 * hash21(vec2(id, 2.0)));
      float bob = (0.5 - 0.5 * cos(uBeatPhase * TAU + hash21(vec2(id, 3.0)) * 1.5)) * 0.012 * uP[3] * (0.5 + uBass);
      vec2 head = vec2(cx, base + h + bob - fr * 0.0);
      float hr = 0.032 * scale * (0.9 + 0.2 * hash21(vec2(id, 4.0)));
      d = min(d, length(p - head) - hr);
      // Shoulders and body.
      d = min(d, capsule(p, vec2(cx - hr * 1.5, head.y - hr * 2.2), vec2(cx + hr * 1.5, head.y - hr * 2.2), hr * 1.1));
      d = min(d, max(abs(p.x - cx) - hr * 2.3, p.y - (head.y - hr * 2.0)));
      // Hands up for some of them, more on the beat.
      if (hash21(vec2(id, 5.0)) < uP[2] * (0.7 + 0.6 * pulse())) {
        float sideA = hash21(vec2(id, 6.0)) < 0.5 ? -1.0 : 1.0;
        vec2 sh = vec2(cx + sideA * hr * 1.4, head.y - hr * 1.8);
        vec2 hand = sh + vec2(sideA * hr * (0.8 + hash21(vec2(id, 7.0))), hr * (4.0 + 1.5 * hash21(vec2(id, 8.0))) + bob * 2.0);
        d = min(d, capsule(p, sh, hand, hr * 0.42));
        d = min(d, length(p - hand) - hr * 0.62);
      }
    }
  }
  float shadow = smoothstep(0.003, -0.002, d);
  // White everywhere (no change under Multiply), dark where people are.
  fragColor = vec4(vec3(1.0 - 0.97 * shadow), 1.0);
}`,
};

export const LASER_SHOW_VISUALIZATIONS: VisualizationDef[] = [laserShow, liquidSky, crowd];
